import {
  Card,
  Checkbox,
  CustomDatePicker,
  Fieldset,
  Form,
  Grid,
  Note,
  Spacer,
} from '@components/common'
import { Controller, FormProvider } from 'react-hook-form'
import { DEPREC_PROFILES, DEPRECIATION_HISTORY_REPORT_PATH } from '@constants'
import ReportFilters from '../ReportFilters'
import { defaultReportFilterFields } from '../ReportFilters/report-filter-fields'
import { DeprecHistoryReportParams } from '@api/models'
import { DepProfileSelect, ActionButtons } from '../General'
import { useReport } from '@hooks'
import { useEffect, useState } from 'react'
import { getTodaysDate, isNullOrUndefined } from '@utils/basic'
import { reportResponseHandler } from '@utils/reports'
import { ReportsApi, useGetSettingsQuery } from '@api'
import { useMemorizeReport } from '../General/MemorizeReports/useMemorizeReport'
import MemorizeReports from '../General/MemorizeReports/MemorizeReport'
import { useLocation } from 'react-router-dom'
import { useFormMethods } from '../General/useFormMethods'

const defaultValues: DeprecHistoryReportParams = {
  assetCompanyID: 0,
  filter: [],
  profile: DEPREC_PROFILES.CORPORATE,
  fromDate: getTodaysDate(),
  toDate: getTodaysDate(),
  includeCaps: false,
  includeAccCode: false,
  includeDeprSys: false,
  includeDeprUsr: false,
  includeYr: false,
  includeDisposals: false,
  includeAssetDesc: false,
  includeUndeprecAssets: false,
  includeDeprecAsOfDate: false,
  fileFormat: 'pdf',
}

const DeprecHistoryForm = () => {
  const location = useLocation()
  const locationState: any = location.state

  const {
    methods,
    isRetrievingTemplate,
    isEditTemplate,
    selectedTemplateDetails,
  } = useFormMethods(locationState, defaultValues)

  const {
    formState: { isSubmitting, errors },
    control,
    setValue,
    watch,
  } = methods

  const {
    isLoading,
    isSuccess,
    isError,
    isNoCompanies,
    depProfilesMap,
    assetCompanyID,
  } = useReport()

  const { data: DateFormat } = useGetSettingsQuery('DateFormat')

  const {
    handleMemorizeTemplateSubmit,
    isMemorizeTemplateModalOpen,
    closeMemorizeTemplateModal,
    triggerMemorizeTemplateModal,
    handleEditTemplateSubmit,
  } = useMemorizeReport({ methods, currentReportPath: DEPRECIATION_HISTORY_REPORT_PATH })

  const [formErrorMsg, setFormErrorMsg] = useState('')

  const fileFormat = watch('fileFormat')
  const totalsOnly = watch('totalsOnly')

  useEffect(() => {
    if (isSuccess) {
      setValue('assetCompanyID', assetCompanyID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, assetCompanyID])

  useEffect(() => {
    if (fileFormat !== 'pdf' && totalsOnly) {
      setValue('totalsOnly', false)
    }
  }, [fileFormat])

  function getCheckBoxProps(field: any) {
    return {
      checked: field.value,
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  function getDateProps(field: any) {
    return {
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
      select: field.value,
      onBlur: field.onBlur,
      error: errors[field] !== undefined,
      errorMsg: errors[field]?.message,
    }
  }

  async function onSubmitHandler(values: DeprecHistoryReportParams) {
    await ReportsApi.getInstance()
      .getDeprecHistoryReport(values)
      .then(data => reportResponseHandler(data, values.fileFormat))
      .catch(err => err.response.data.text())
      .then(text => setFormErrorMsg(text))
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error</div>
  }

  if (isNoCompanies) {
    return <div>No companies found</div>
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmitHandler)} key={1}>
          <Card title={'Report Parameters'}>
            <Grid>
              {!isNullOrUndefined(formErrorMsg) && formErrorMsg !== '' ? (
                <Grid.Item>
                  <Note intent="danger" content={<>{formErrorMsg}</>} />
                </Grid.Item>
              ) : (
                ''
              )}
              <Grid.Item>
                <DepProfileSelect depProfilesMap={depProfilesMap} />
              </Grid.Item>
              <Grid.Item>
                <Fieldset legend="Date Range">
                  <Grid>
                    <Grid.Item s={6}>
                      <Controller
                        control={control}
                        name="fromDate"
                        render={({ field }) => (
                          <CustomDatePicker {...getDateProps(field)} label="From" />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item s={6}>
                      <Controller
                        control={control}
                        name="toDate"
                        render={({ field }) => (
                          <CustomDatePicker {...getDateProps(field)} label="To" />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item s={6}>
                <Fieldset legend={'Additional Columns to Include'}>
                  <Grid rowGap="mini">
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeAssetDesc"
                        render={({ field }) => (
                          <Checkbox
                            label="Include asset description"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDeprecAsOfDate"
                        render={({ field }) => (
                          <Checkbox
                            label="Include depreciation as of date"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeAccCode"
                        render={({ field }) => (
                          <Checkbox
                            label="Include acceleration code"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDeprSys"
                        render={({ field }) => (
                          <Checkbox
                            label="Include depreciation system code"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDeprUsr"
                        render={({ field }) => (
                          <Checkbox
                            label="Include depreciation user code"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeYr"
                        render={({ field }) => (
                          <Checkbox label="Include years" {...getCheckBoxProps(field)} />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item s={6}>
                <Fieldset legend={'Options'}>
                  <Grid>
                    {fileFormat === 'pdf' && (
                      <Grid.Item>
                        <Controller
                          control={control}
                          name="totalsOnly"
                          render={({ field }) => (
                            <Checkbox label="Totals only" {...getCheckBoxProps(field)} />
                          )}
                        />
                      </Grid.Item>
                    )}
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeUndeprecAssets"
                        render={({ field }) => (
                          <Checkbox
                            label="Include undepreciated assets"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeDisposals"
                        render={({ field }) => (
                          <Checkbox
                            label="Include disposals"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeCaps"
                        render={({ field }) => (
                          <Checkbox
                            label="Include capitalizations"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item>
                <ReportFilters
                  filtersTemplateData={
                    isRetrievingTemplate
                      ? locationState.templateDetails.templateData.filter
                      : []
                  }
                  filterFields={defaultReportFilterFields}
                  includesTotals={fileFormat === 'pdf'}
                />
              </Grid.Item>
            </Grid>
          </Card>
          <Spacer y="default" />
          <ActionButtons
            isSubmitting={isSubmitting}
            triggerMemorizeTemplateModal={triggerMemorizeTemplateModal}
            isEditTemplate={isEditTemplate}
          />
        </Form>
      </FormProvider>

      <MemorizeReports
        isMemorizeTemplateModalOpen={isMemorizeTemplateModalOpen}
        closeMemorizeTemplateModal={closeMemorizeTemplateModal}
        handleMemorizeTemplateSubmit={handleMemorizeTemplateSubmit}
        handleEditTemplateSubmit={handleEditTemplateSubmit}
        isEditTemplate={isEditTemplate}
        selectedTemplateDetails={selectedTemplateDetails}
      />
    </>
  )
}

export default DeprecHistoryForm
